import React, {
  useMemo, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'utils/modifiedTranslation';
import { ReactComponent as DiagnosticIcon } from 'assets/icons/fluent_pulse.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search_rounded.svg';
import { ReactComponent as TreatmentIcon } from 'assets/icons/maki_doctor.svg';
import { ReactComponent as RecoveryIcon } from 'assets/icons/mdi_heart.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as CircleIcon } from 'assets/icons/circle_subtract.svg';
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow_left_2.svg';
import { Preloader } from 'components/Preloader';
import app from 'new-ui/app';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import ActionCard from '../ActionCard/ActionCard';
import styles from './StepJourenyCard.module.css';
import { evaluateActionConditions } from '../nbaUtils';

const MILESTONE_ICONS = {
  diagnostic: DiagnosticIcon,
  'treatment search': SearchIcon,
  'on treatment': TreatmentIcon,
  recovery: RecoveryIcon,
};

const ACTIONS_PER_PAGE = 1;
const MAX_VISIBLE_SUB_STEPS = 3;

const StepJourneyCard = ({
  milestones = [],
  activeMilestoneId = null,
  selectedStepId = null,
  completedMilestoneIds = [],
  onMilestoneClick = () => {},
  onStepClick = () => {},
  onActionClick = () => {},
  isActionLoading = false,
  dynamicActionComponent = null,
  onPagedActionChange = () => {},
  evaluationContext = {},
}) => {
  const { t } = useTranslation();
  const [currentActionPage, setCurrentActionPage] = useState(1);
  const [subStepViewIndex, setSubStepViewIndex] = useState(0);

  const getMilestoneIcon = (milestoneName) => {
    const normalizedId = milestoneName?.toLowerCase();
    const IconComponent = MILESTONE_ICONS[normalizedId] || DiagnosticIcon;
    return <IconComponent />;
  };

  const getStepIcon = (stepName) => (selectedStepId === stepName
    ? <CheckIcon className={styles.subStepSelectedIcon} />
    : <CircleIcon className={styles.subStepIcon} />);

  const activeMilestone = useMemo(() => (
    milestones.find((m) => m.name === activeMilestoneId)
  ), [milestones, activeMilestoneId]);

  const stepsOfActiveMilestone = useMemo(() => (
    activeMilestone?.steps ?? []
  ), [activeMilestone]);

  useEffect(() => {
    setSubStepViewIndex(0);
  }, [activeMilestoneId]);

  const totalSubSteps = stepsOfActiveMilestone.length;
  const canNavigateSubSteps = totalSubSteps > MAX_VISIBLE_SUB_STEPS;

  const currentVisibleSubSteps = useMemo(() => {
    if (!canNavigateSubSteps) return stepsOfActiveMilestone;
    return stepsOfActiveMilestone.slice(subStepViewIndex, subStepViewIndex + MAX_VISIBLE_SUB_STEPS);
  }, [stepsOfActiveMilestone, subStepViewIndex, canNavigateSubSteps]);

  const showPrevSubStepArrow = canNavigateSubSteps && subStepViewIndex > 0;
  const showNextSubStepArrow = canNavigateSubSteps && subStepViewIndex < totalSubSteps - MAX_VISIBLE_SUB_STEPS;

  const handlePrevSubStepView = useCallback(() => setSubStepViewIndex((prev) => Math.max(0, prev - 1)), []);
  const handleNextSubStepView = useCallback(() => setSubStepViewIndex((prev) => Math.min(totalSubSteps - MAX_VISIBLE_SUB_STEPS, prev + 1)), [totalSubSteps]);

  const currentStepData = useMemo(() => (
    stepsOfActiveMilestone.find((s) => s.name === selectedStepId)
  ), [stepsOfActiveMilestone, selectedStepId]);

  const actionsOfCurrentStep = useMemo(() => {
    const allActionsRaw = (currentStepData?.actions ?? []);
    const allActionsOrdered = allActionsRaw.map((action, index) => ({
      ...action,
      order: typeof action.order === 'number' ? action.order : index + 1,
    })).sort((a, b) => a.order - b.order);

    return allActionsOrdered.filter((action) => evaluateActionConditions(action.conditions, evaluationContext));
  }, [currentStepData, evaluationContext, selectedStepId]);

  useEffect(() => {
    setCurrentActionPage(1);
  }, [selectedStepId, actionsOfCurrentStep]);

  const totalActions = actionsOfCurrentStep.length;
  const actionStartIndex = (currentActionPage - 1) * ACTIONS_PER_PAGE;
  const displayedAction = actionsOfCurrentStep[actionStartIndex];

  useEffect(() => {
    if (displayedAction) {
      onPagedActionChange(displayedAction.name);
    } else if (totalActions === 0 && currentActionPage === 1) {
      onPagedActionChange(null);
    }
  }, [displayedAction, onPagedActionChange, totalActions, currentActionPage]);

  useEffect(() => {
    if (
      displayedAction?.onActionCallIdentifier === 'renderComponent'
      && !isActionLoading
      && (!dynamicActionComponent || dynamicActionComponent.originatingActionName !== displayedAction.name)
    ) {
      onActionClick(displayedAction);
    }
  }, [displayedAction, onActionClick, isActionLoading, dynamicActionComponent]);

  const handlePrevActionPage = useCallback(() => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_NAVIGATOR_CLICKED,
      { direction: 'previous' },
    );
    setCurrentActionPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextActionPage = useCallback(() => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_NAVIGATOR_CLICKED,
      { direction: 'next' },
    );
    setCurrentActionPage((prev) => Math.min(Math.ceil(totalActions / ACTIONS_PER_PAGE), prev + 1));
  }, [totalActions]);
  const handleActionExecution = useCallback(async (actionToExecute) => {
    await onActionClick(actionToExecute);
    if (actionToExecute.autoAdvance === false) {
      return;
    }
    const isLastActionInStep = !(actionStartIndex + ACTIONS_PER_PAGE < totalActions);

    if (isLastActionInStep) {
      const currentStepIndex = stepsOfActiveMilestone.findIndex((step) => step.name === selectedStepId);

      if (currentStepIndex !== -1 && currentStepIndex < stepsOfActiveMilestone.length - 1) {
        const nextStep = stepsOfActiveMilestone[currentStepIndex + 1];
        if (nextStep && nextStep.name) {
          onStepClick(nextStep.name);
        }
      }
    } else {
      handleNextActionPage();
    }
  }, [
    onActionClick,
    handleNextActionPage,
    onStepClick,
    totalActions,
    actionStartIndex,
    stepsOfActiveMilestone,
    selectedStepId,
  ]);

  const renderActionArea = () => {
    if (isActionLoading) return <div className={styles.actionLoadingPlaceholder}><Preloader isVisible inline /></div>;
    if (displayedAction) {
      let componentToRender = dynamicActionComponent;
      if (dynamicActionComponent) {
        const canAdvanceToAction = actionStartIndex + ACTIONS_PER_PAGE < totalActions;
        const currentStepIndex = stepsOfActiveMilestone.findIndex((step) => step.name === selectedStepId);
        const canAdvanceToStep = currentStepIndex !== -1 && currentStepIndex < stepsOfActiveMilestone.length - 1;

        if (canAdvanceToAction || canAdvanceToStep) {
          componentToRender = {
            ...dynamicActionComponent,
            props: {
              ...dynamicActionComponent.props,
              onCompleteAndAdvance: () => {
                if (canAdvanceToAction) {
                  handleNextActionPage();
                } else if (canAdvanceToStep) {
                  const nextStep = stepsOfActiveMilestone[currentStepIndex + 1];
                  if (nextStep && nextStep.name) {
                    onStepClick(nextStep.name);
                  }
                }
              },
            },
          };
        }
      }

      return (
        <ActionCard
          key={`${activeMilestoneId}_${selectedStepId}_action_${displayedAction.name}_${actionStartIndex}`}
          title={displayedAction.title || 'What to do next?'}
          buttonText={displayedAction.name}
          buttonAction={() => handleActionExecution(displayedAction)}
          description={displayedAction.description || `Complete the action: ${displayedAction.name}`}
          linkText={displayedAction.linkText || 'Need our help?'}
          actionName={displayedAction.name}
          showPrev={currentActionPage > 1}
          showNext={actionStartIndex + ACTIONS_PER_PAGE < totalActions}
          onPrev={handlePrevActionPage}
          onNext={handleNextActionPage}
          dynamicContentComponent={componentToRender}
          isDynamicContentSource={dynamicActionComponent?.originatingActionName === displayedAction.name && displayedAction?.onActionCallIdentifier === 'renderComponent'}
        />
      );
    }
    if (totalActions === 0 && currentStepData) {
      return <div className={styles.noActionsMessage}>{t('nba.no_actions_for_step')}</div>;
    }
    return null;
  };

  return (
    <div>
      <div className={styles.stepJourneyCard}>
        {Array.isArray(milestones) && milestones.length > 0 ? (
          <div className={styles.stepRow}>
            {milestones.map((milestone, index) => {
              if (!milestone || typeof milestone.name !== 'string') return null;
              const isActive = milestone.name === activeMilestoneId;
              const isCompleted = completedMilestoneIds.includes(milestone.name);
              const isDisabled = milestone.isDisabled === true;
              return (
                <React.Fragment key={milestone.name}>
                  <button
                    type="button"
                    className={
                      cx(styles.step, { [styles.activeStep]: isActive, [styles.completedStep]: isCompleted })
                    }
                    onClick={() => !isDisabled && onMilestoneClick(milestone.name)}
                    disabled={isDisabled}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <div className={styles.stepIconWrapper}>{getMilestoneIcon(milestone.name)}</div>
                    <span className={styles.stepLabel}>{milestone.name}</span>
                  </button>
                  {index < milestones.length - 1 && <div className={styles.connector} />}
                </React.Fragment>
              );
            })}
          </div>
        ) : <div className={styles.stepRow}>No milestones to display.</div>}

        {Array.isArray(stepsOfActiveMilestone) && stepsOfActiveMilestone.length > 0 && (
          <div className={styles.subStepNavContainer}>
            {showPrevSubStepArrow && <button type="button" className={cx(styles.subStepArrow, styles.subStepArrowPrev)} onClick={handlePrevSubStepView} aria-label={t('nba.previous_sub_steps')} disabled={!showPrevSubStepArrow}><ArrowLeft /></button>}
            <div className={styles.subStepRow}>
              {(canNavigateSubSteps ? currentVisibleSubSteps : stepsOfActiveMilestone).map((step, index, arr) => {
                if (!step || typeof step.name !== 'string') return null;
                const isSelected = step.name === selectedStepId;
                return (
                  <React.Fragment key={step.name}>
                    <button type="button" className={cx(styles.subStep, { [styles.selectedSubStep]: isSelected })} onClick={() => onStepClick(step.name)} aria-pressed={isSelected}>
                      <div className={styles.subStepIconWrapper}>{getStepIcon(step.name)}</div>
                      <span className={styles.subStepLabel}>{step.name}</span>
                    </button>
                    {index < arr.length - 1 && <div className={styles.subConnector} />}
                  </React.Fragment>
                );
              })}
            </div>
            {showNextSubStepArrow && <button type="button" className={cx(styles.subStepArrow, styles.subStepArrowNext)} onClick={handleNextSubStepView} aria-label={t('nba.next_sub_steps')} disabled={!showNextSubStepArrow}><ArrowLeft style={{ transform: 'rotate(180deg)' }} /></button>}
          </div>
        )}
      </div>
      <div className={styles.actionsContainer}>{renderActionArea()}</div>
    </div>
  );
};

StepJourneyCard.propTypes = {
  milestones: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      order: PropTypes.number,
      actions: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        order: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        linkText: PropTypes.string,
        onActionCallIdentifier: PropTypes.string,
        args: PropTypes.object,
        renderAnotherComponent: PropTypes.string,
        renderAnotherComponentArgs: PropTypes.object,
        conditions: PropTypes.object,
        autoAdvance: PropTypes.bool,
      })),
    })),
  })),
  activeMilestoneId: PropTypes.string,
  selectedStepId: PropTypes.string,
  completedMilestoneIds: PropTypes.arrayOf(PropTypes.string),
  onMilestoneClick: PropTypes.func,
  onStepClick: PropTypes.func,
  onActionClick: PropTypes.func,
  isActionLoading: PropTypes.bool,
  dynamicActionComponent: PropTypes.shape({
    Component: PropTypes.elementType,
    props: PropTypes.object,
    originatingActionName: PropTypes.string,
  }),
  onPagedActionChange: PropTypes.func,
  evaluationContext: PropTypes.object,
};

export default StepJourneyCard;
